/**
 * Browser push, as the Flutter console had it (services/push_service.dart): after
 * sign-in the browser asks for permission, mints a Firebase Cloud Messaging token and
 * registers it with POST /devices/register; on sign-out the token is retired with
 * POST /devices/unregister, so a signed-out browser gets no alerts.
 *
 * Push is a bonus, never a gate: an unsupported browser, a refused prompt or a failed
 * call leaves the console exactly as it was. Firebase is loaded only when a token is
 * minted, so it stays out of the main bundle.
 */
import { deviceRegister, deviceUnregister, type ApiClient } from '@aithora/shared';

/** Web config for project pngn-prod-2026 (public identifiers, as in the Flutter console). */
const FIREBASE_WEB = {
  apiKey: (import.meta.env.VITE_FIREBASE_API_KEY as string | undefined) ?? 'AIzaSyAXP4fJMS8AfwFcn10gDyPGe9z2WAVdW3M',
  appId: (import.meta.env.VITE_FIREBASE_APP_ID as string | undefined) ?? '1:800809436452:web:422994cf5c4b8a68271fb1',
  messagingSenderId: (import.meta.env.VITE_FIREBASE_SENDER_ID as string | undefined) ?? '800809436452',
  projectId: (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) ?? 'pngn-prod-2026',
  authDomain: 'pngn-prod-2026.firebaseapp.com',
  storageBucket: 'pngn-prod-2026.firebasestorage.app',
};
/** The VAPID public key the browser subscribes with. */
const VAPID_KEY =
  (import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined) ??
  'BMv5tiCiAy2DRLbO81ZTC6CAhzHr0mAntUlZMWFhkxHSVQ3istJbjRvqueVyPFdfe4klpTIdtKbBEyjzMkOqgHo';

/** The service worker that shows a push while the tab is closed (public/push-sw.js). */
export const PUSH_SW_URL = '/push-sw.js';
/** Its own scope, so it never controls the console's pages. */
const PUSH_SW_SCOPE = '/aithora-push/';
const TOKEN_KEY = 'aithora.console.pushToken';

/** Mints this browser's push token, or null when it cannot. Replaced in tests. */
export type TokenMinter = () => Promise<string | null>;

async function firebaseMint(): Promise<string | null> {
  const [{ getApps, initializeApp }, { getMessaging, getToken, isSupported }] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
  if (!(await isSupported())) return null;
  const app = getApps()[0] ?? initializeApp(FIREBASE_WEB);
  const registration = await navigator.serviceWorker.register(PUSH_SW_URL, { scope: PUSH_SW_SCOPE });
  const token = await getToken(getMessaging(app), { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  return token || null;
}

let mint: TokenMinter = firebaseMint;
let registered: string | null = null;

/** Tests swap the Firebase minting for their own. */
export function setTokenMinter(m: TokenMinter | null): void {
  mint = m ?? firebaseMint;
}

function store(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

// The last token this browser registered, kept across tabs and sign-outs so a new token
// replaces it on the server instead of listing the same browser twice.
const LAST_KEY = 'aithora.console.pushTokenLast';
function lastToken(): string | null {
  try { return typeof window !== 'undefined' ? window.localStorage.getItem(LAST_KEY) : null; } catch { return null; }
}
function rememberToken(token: string): void {
  try { window.localStorage.setItem(LAST_KEY, token); } catch { /* storage blocked: the server keeps both */ }
}

/** "Chrome on Windows · app.aithoracare.com": how this browser reads in the device list. */
export function browserLabel(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '', host: string = typeof location !== 'undefined' ? location.host : ''): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return (os ? `${browser} on ${os}` : browser) + (host ? ` · ${host}` : '');
}

/** First 16 hex of sha256(token), the same as the server's tokenHash. */
export async function pushTokenHash(token: string): Promise<string | null> {
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
  } catch {
    return null;
  }
}

/** The token this tab registered, kept for sign-out across a reload. */
export function registeredPushToken(): string | null {
  return registered ?? store()?.getItem(TOKEN_KEY) ?? null;
}

function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export type PushOutcome = 'registered' | 'unsupported' | 'denied' | 'not-asked' | 'failed';

/**
 * Register this browser for push. `prompt` asks for permission when it has not been
 * given yet (right after sign-in, or the Allow push button); without it, only a browser
 * that already said yes is registered (a restored session, which also refreshes the token).
 */
export async function registerForPush(client: ApiClient, { prompt }: { prompt: boolean }): Promise<PushOutcome> {
  if (!pushSupported()) return 'unsupported';
  try {
    let perm = Notification.permission;
    if (perm === 'default') {
      if (!prompt) return 'not-asked';
      perm = await Notification.requestPermission();
    }
    if (perm !== 'granted') return 'denied';
    const token = await mint();
    if (!token) return 'unsupported';
    const last = lastToken();
    await deviceRegister(client, { body: { token, platform: 'web', name: browserLabel(), ...(last && last !== token ? { replaces: last } : {}) } });
    registered = token;
    store()?.setItem(TOKEN_KEY, token);
    rememberToken(token);
    return 'registered';
  } catch {
    return 'failed';
  }
}

/** Retire this browser's token (sign-out). Best effort: it never blocks signing out. */
export async function unregisterPush(client: ApiClient): Promise<void> {
  const token = registeredPushToken();
  registered = null;
  try {
    store()?.removeItem(TOKEN_KEY);
  } catch {
    /* nothing stored */
  }
  if (!token) return;
  try {
    await deviceUnregister(client, { body: { token } });
  } catch {
    /* the session ends regardless */
  }
}
